import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useState, useEffect } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import ViewAsUser from '../ViewAsUser';
import { useHMG } from '../../../HMGContext';
import dataService from '../../../services/data.service';
import helper from '../../../services/helper';
import LoadingScreen from '../LoadingScreen';
import Http from '../../../services/Http';
import { handleApiError } from '../../../Utils/errorHandler';

const ViewAsDialog = ({ open, onClose, setAlert  }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    // const [showViewAsUser, setShowViewAsUser] = useState(false);

    const {handleViewAsUserOpen , setCurrentModalName, viewAsLoading, setViewAsLoading, selectedHMG} = useHMG();
const handleViewAs = async () => {
    if (!selectedUser) {
        setError('Please select a user to view as.');
        return;
    }
    setError('');
    onClose();
    setCurrentModalName(null);
    try {
        setViewAsLoading(true);
        await handleViewAsUserOpen(selectedUser);

    } catch (error) {
        setError('Failed to assume user. Please try again.');
        setViewAsLoading(false);
    }
};


    const handleClose = () => {
        setSelectedUser(null);
        setError('');
        onClose();
        setCurrentModalName(null); 
    };

    const handleUserChange = (event, newValue) => {
        setSelectedUser(newValue);
        if (error) {
            setError(''); // Clear error when user selects an option
        }
    };

    useEffect(() => {
        if (open) {
            setSelectedUser(null);
            setError('');
            
            // Fetch users only when dialog opens
            const fetchUsers = async () => {
                if (!dataService.getSelectedHMGId()) {
                    setError('HMG ID is not available. Please select an organization.');
                    return;
                }

                try {
                    setLoadingUsers(true);
                    setError('');
                    const response = await Http.GET(`/user/hmg-users?hmgId=${dataService.getSelectedHMGId()}`);
                    
                    if (response && response.status === 200 && Array.isArray(response.data)) {
                        // Transform response: userEmail as id, userName as label
                        const transformedUsers = response.data.map(user => ({
                            id: user.userEmail,
                            label: user.userName
                        }));
                        setUserOptions(transformedUsers);
                    } else {
                        setError('Failed to load users. Please try again.');
                        setUserOptions([]);
                    }
                } catch (error) {
                    console.error('Error fetching users:', error);
                    // Use global error handler for consistent logging, then set local error state
                    handleApiError(error, () => {}, "", { showToast: false });
                    setError('Failed to load users. Please try again.');
                    setUserOptions([]);
                } finally {
                    setLoadingUsers(false);
                }
            };

            fetchUsers();
        } else {
            // Clear user options when dialog closes
            setUserOptions([]);
        }
    }, [open, dataService.getSelectedHMGId()]);
    
    return (
        <>
            {viewAsLoading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        zIndex: 9999,
                    }}
                ><LoadingScreen position="fixed" title={`Redirecting to "View as" mode.`} /></Box>
            )}
            <Dialog open={open} fullWidth maxWidth="sm">
                <div className='!p-4'>
                    <DialogTitle className='!p-0'>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                variant="h4"
                                sx={{
                                    fontSize: "24px",
                                    lineHeight: "36px",
                                    fontWeight: 700,
                                    color: "#212B36",
                                }}
                            >
                                View as
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <ClearIcon sx={{ width: "16px", height: "16px" }} />
                            </IconButton>
                        </Box>
                        <Typography className='!text-xs !font-normal text-[#637381]'>
                            *Required fields
                        </Typography>
                    </DialogTitle>
                    <DialogContent className='!p-0'>
                        <Alert
                            variant="standard"
                            severity="warning"
                            icon={<ErrorIcon className="mt-1" sx={{ color: '#00B8D9' }} />}
                            sx={{
                                color: '#003768',
                                borderColor: '#CAFDF5',
                                border: 'none',
                                minHeight: '50px',
                                backgroundColor: '#CAFDF5',
                                fontSize: '14px',
                                lineHeight: '22px',
                                display: 'flex',
                                padding: '0 16px',
                                margin: '16px 0px',
                                '& .MuiAlert-icon': {
                                    color: '#8E33FF',
                                },
                                '& .MuiAlert-message': {
                                    width: '100%',
                                },
                            }}
                        >
                            You are about to view the system as the selected user. Any actions you perform will be tagged as "Updated by {`${helper.getNameWithLastNameAsInitial(dataService.getFullUserName())}`}"
                        </Alert>
                        <Autocomplete
                            disableClearable
                            size="small"
                            options={userOptions}
                            value={selectedUser}
                            onChange={handleUserChange}
                            getOptionLabel={(option) => option.label}
                            loading={loadingUsers}
                            disabled={loadingUsers}
                            renderInput={(params) => (
                                <TextField 
                                    {...params} 
                                    label="User Name" 
                                    fullWidth 
                                    error={!!error}
                                    helperText={error}
                                    sx={{
                                        ".MuiOutlinedInput-root": { fontSize: "14px" },
                                        ".MuiInputLabel-root": { fontSize: "14px" }
                                    }} 
                                />
                            )}
                            className='mb-4'
                        />
                    </DialogContent>
                    <DialogActions
                        sx={{ justifyContent: "end" }}
                        className="!p-0"
                    >
                        <Button onClick={handleClose} variant="outlined" sx={{ color: "#212B36", borderColor: "#919EAB52" }} className="!text-sm !font-bold !px-3">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleViewAs}>
                            View as 
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
            {/* {showViewAsUser && <ViewAsUser userName={selectedUser?.label} onExitView={handleExitView} />} */}
        </>
    );
};

export default ViewAsDialog;